import AssessmentGymCharts from '@components/Charts/AssessmentGymCharts'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useSlugLinks } from '@hooks/useSlugLinks'
import { useQuery } from '@tanstack/react-query'
import {
  GetAPIparams,
  renderTitle,
  useDropdownMenu
} from '@utils/assessmentReportUtils'
import { appSessionStorage } from '@utils/storageUtils'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useLocation } from '@suntex.core/nav'
import { TeamId } from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  getValidAssessmentOption,
  schoolAssessmentInitialSorting,
  teamAssessmentInitialSorting
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $ArithmeticOperation,
  $EntityLevel,
  $GymMode,
  EntityLevel,
  GymMode
} from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput,
  useTheme
} from '@suntex.fim/ui'

import {
  GymReportData,
  columns,
  getGymColumnKeys,
  hidableSelectLabel,
  schoolColumns,
  schoolHidableSelectLabel
} from './AssessmentReport/AssessmentReportGymTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import FactFluency from './AssessmentReport/FactFluency'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

const gymLevelOptions = $GymMode.listOptions()

/**
 * returns JSX for gym report
 */
function AssessmentReportGym() {
  const { api } = useFramework()
  const theme = useTheme()
  useRotateDevice()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { state, pathname } = useLocation()
  const { gymLevel: queryGymLevel } = useAppQueryString()
  const { login } = useAuth(isAuthenticated)
  const { getGameUrl, isLoading: isSlugLinksLoading } = useSlugLinks()
  const isSchool =
    login.entityLevel === EntityLevel.School && pathname.includes('school')
  const team = useOptionalTeamInfo()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const initiallevel: GymMode = state?.level
    ? getValidAssessmentOption(gymLevelOptions, state.level, GymMode.K2)
    : GymMode.WholeNumbers

  const selectedLevel =
    $GymMode.fromOptional(queryGymLevel) ||
    $GymMode.fromOptional(appSessionStorage.gymLevel) ||
    initiallevel
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()

  const { id, year, grade } = GetAPIparams(isSchool, dropdownFilters)
  const [filters, setFilters] = useState({
    level: getValidAssessmentOption(
      gymLevelOptions,
      selectedLevel,
      initiallevel
    )
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  // Update localStorage whenever level changes
  useEffect(() => {
    appSessionStorage.gymLevel = filters.level
  }, [filters.level])

  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const memoColumns = useMemo<ColumnType<GymReportData>[]>(
    () =>
      isSchool
        ? schoolColumns({
            level: filters.level
          })
        : columns({
            level: filters.level,
            graphColor: theme.colors.gyms,
            sendMessage
          }),
    [filters.level]
  )
  const columnKeys = getGymColumnKeys(filters.level)
  const linkOptions = columnKeys.map((label) => {
    const url = getGameUrl([
      'Gyms',
      hidableSelectLabel[label] ?? '',
      gymLevelOptions[filters.level]?.label ?? ''
    ])
    return {
      key: url,
      label: hidableSelectLabel[label] ?? ''
    }
  })
  const { data: summaryRecordData = {} } = useQuery({
    queryKey: ['assessment', 'gymSummary', id, isSchool, year, grade],
    queryFn: async () => {
      const response =
        await api.get_Assessment_GymsFactFluencyPerformanceAggregateByLevels({
          id: id!,
          level: isSchool ? login.entityLevel : EntityLevel.Team,
          year: year!,
          aggregateLevel: login.entityLevel,
          grade
        })
      return response.data?.[0] || {}
    },
    enabled: id != null && year != null && login.entityLevel != null
  })

  const { data: assessmentReportData, isLoading } = useQuery({
    queryKey: ['assessment', 'gym', id, isSchool, filters.level, year, grade],
    queryFn: async () => {
      if (isSchool && login.entityLevel) {
        const response =
          await api.get_Assessment_GymsFactFluencyPerformanceAggregateByLevels({
            id: id!,
            level: login.entityLevel,
            year: year!,
            aggregateLevel: $EntityLevel.from(login.entityLevel - 1),
            grade
          })
        return response.data || []
      } else {
        const response = await api.get_Assessment_GymsFactFluencyTeam({
          teamId: id as TeamId,
          mode: filters.level
        })
        return response.gymFluencyDetails || []
      }
    },
    enabled: id != null
  })
  const assessmentReport = getAssessmentReportData(assessmentReportData)
  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } = useAssessmentTable<GymReportData>({
    data: assessmentReport,
    columns: memoColumns,
    isLoading,
    horizontalScroll: true,
    hideFirstTwoColumns: !isSchool,
    hideFirstColumns: !isSchool,
    globalFilter: searchQuery,
    initialSorting: isSchool
      ? schoolAssessmentInitialSorting
      : teamAssessmentInitialSorting
  })
  const selectedRow = table.getSelectedRowModel().flatRows
  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: GymReportData }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )
  const fileName = isSchool ? `${login.schoolName}_gym` : `${team?.name}_gym`
  const exportColumn = isSchool ? schoolHidableSelectLabel : hidableSelectLabel
  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool
      ? ['grade']
      : $ArithmeticOperation.describeAll().map((op) => op.label),
    transformCols: isSchool
      ? () => {}
      : (id: string, colValue: any) => {
          const keys = Array.from({ length: 12 }, (_, i) => (i + 1).toString())
          const updatedColValue = keys.reduce(
            (acc, key) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              acc[key] = colValue?.[key] || false
              return acc
            },
            {} as Record<string, boolean>
          )

          if (filters.level === GymMode.K2) {
            delete updatedColValue['11']
            delete updatedColValue['12']
          }

          return Object.entries(updatedColValue).reduce(
            (newValue, [key, value]) => {
              return {
                ...newValue,
                [`${hidableSelectLabel[id][0]}${key}`]: value ? 1 : 0
              }
            },
            {}
          )
        },
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex
        className="assessment-report-gym"
        vertical
        gap="20px"
        style={{ position: 'relative' }}
      >
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay
          isLoading={isLoading || isExporting || isSlugLinksLoading}
        />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          filters={
            <Flex gap={14}>
              <Flex flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Level</p>
                <LevelSelect
                  value={filters.level}
                  onChange={(level) => {
                    setFilters((prev) => ({
                      ...prev,
                      level: $GymMode.from(Number(level))
                    }))
                    setSearchQuery('')
                  }}
                  options={gymLevelOptions}
                />
              </Flex>
            </Flex>
          }
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropDownFilters}
          isSchool={isSchool}
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <FactFluency type="purple" />
            <AssessmentReportSummaryDashboard
              dropdownFilters={dropdownFilters}
              isSchool={isSchool}
            />
          </Flex>
          <Flex
            className="right right-graph-box"
            flex="1 1 560px"
            style={{ height: '235px' }}
          >
            <Flex vertical className="graph-tiles" justify="space-between">
              {renderTitle(isSchool)}
              <Flex vertical align="center" className="graph-tiles__graph">
                <div className="graph-container">
                  <AssessmentGymCharts
                    backgroundColor={theme.colors.gyms}
                    chartData={summaryRecordData}
                    level={filters.level}
                  />
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex
              gap="10px"
              justify="space-between"
              wrap
              className="print-hidden"
            >
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {!isSchool && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
            aria-label="Selected rows actions popup"
          />
        )}
        <SendMessage
          level={gymLevelOptions[filters.level]?.label}
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Gyms (Fact Fluency)"
        />
      </Flex>
    </>
  )
}

export default AssessmentReportGym
